import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'
import axios from 'axios'
import CSVReader from '../../components/CSVReaderClickAndDragUpload'
import ManualUploadMapper from '../../forms/AddCustomers/UploadCustomers/ManualUploadMapper'
import { useModal } from '../../components/useModal'
import { Pill } from '@demandlocal/components'
import { ReactComponent as IconLock } from '../../images/icons/icon_lock.svg'
import { ReactComponent as UploadComplete } from '../../images/secure_upload/upload_complete.svg'
import { motion } from 'framer-motion'
import moment from 'moment'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import dayjsDuration from 'dayjs/plugin/duration'
dayjs.extend(utc)
dayjs.extend(dayjsDuration)

const SecureUpload = (): JSX.Element => {
  const { handleModal } = useModal()
  const [search] = useSearchParams()
  const [uploadComplete, setUploadComplete] = useState(false)

  const token = search.get('token')
  const { data } = useQuery(
    ['verify_token', token],
    () =>
      axios
        .get(`${import.meta.env.VITE_API_URL}/public/verify-token`, {
          params: { short_id: token },
        })
        .then((res) => res.data),
    {
      enabled: !!token,
      suspense: true,
      useErrorBoundary: true,
      retry: false,
    },
  )
  const parsedToken = data?.data?.token
  const expirationDate = moment(data?.data?.expiration_date)
  //countdown to expiration date in seconds
  const [countdown, setCountdown] = useState('')
  React.useEffect(() => {
    const interval = setInterval(() => {
      const now = dayjs()
      const diff = dayjs.duration(
        dayjs.utc(data?.data?.expiration_date).local().diff(now),
      )
      // format diff as HH:mm:ss
      const formatted = `${Math.floor(
        Number(diff.asHours()),
      )}:${diff.minutes()}:${diff.seconds()}`
      setCountdown(formatted)
    }, 1000)
    return () => clearInterval(interval)
  }, [countdown, data?.data?.expiration_date, expirationDate])

  const manualUploadCSVMutation = useMutation(
    ({
      file,
      mapped_columns,
      customer_source_id,
    }: {
      file: File
      mapped_columns: any
      customer_source_id: string
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('mapped_columns', JSON.stringify(mapped_columns))
      formData.append('customer_source_id', customer_source_id)
      return axios({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/public/upload-data`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: 'Bearer ' + parsedToken,
        },
      })
    },
  )
  const databaseColumns = data?.data?.columns
  return (
    <div className={'w-screen h-screen flex flex-1'}>
      <div className={'grid grid-rows-5 h-full w-full m-auto py-10 px-10 xl:px-24'}>
        <div className={'flex justify-between'}>
          <div className={'flex items-start'}>
            <div className={'flex items-center gap-4'}>
              <h1 className={'text-3xl font-extrabold text-primary-xiketic'}>
                Securely upload your file
              </h1>
              <Pill type={'success'}>
                <IconLock className={'stroke-[#107E28] fill-[#107E28]'} />
                Secure Upload
              </Pill>
            </div>
          </div>
          <div className={'flex items-start'}>
            {!uploadComplete && (
              <div className={'flex items-center gap-3'}>
                <div className={'text-web-gray font-medium text-xs'}>Available for</div>
                <motion.div
                  className={
                    'text-primary-xiketic text-xl font-bold tabular-nums font-sans'
                  }>
                  {countdown ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}>
                      {countdown}
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}>
                      00:00:00
                    </motion.div>
                  )}
                </motion.div>
              </div>
            )}
          </div>
        </div>
        {uploadComplete && (
          <div className={'flex justify-center items-center row-span-3'}>
            <motion.div layout className={'flex flex-col items-center gap-4'}>
              <UploadComplete />
              <h1 className={'text-3xl font-extrabold text-primary-xiketic'}>
                Files securely uploaded!
              </h1>
            </motion.div>
          </div>
        )}
        {!uploadComplete && (
          <div
            className={
              'bg-cultured-gray w-full rounded-lg row-span-3 flex flex-col items-center justify-center gap-8 py-8'
            }>
            <div className={'flex flex-col items-center w-1/2'}>
              <div className={'font-bold text-base'}>Upload Data File</div>
              <div className={'font-medium pt-2 text-xs text-davy-gray'}>
                Accepted formats: CSV, TSV (50 mb max)
              </div>
            </div>
            <div className={'h-72 w-1/2'}>
              <CSVReader
                onFileAccepted={(results, file) => {
                  handleModal(
                    <ManualUploadMapper
                      selectedUploadType={data?.data?.source_type}
                      // @ts-ignore
                      data={results}
                      file={file}
                      customer_source_id={data?.data?.customer_source_id}
                      manualUploadCSVMutation={manualUploadCSVMutation}
                      databaseColumns={databaseColumns}
                      onUploadComplete={() => {
                        setUploadComplete(true)
                      }}
                    />,
                  )
                }}
              />
            </div>
            <div
              className={
                'flex items-center bg-secondary-malachite-green/30 text-[#107E28] font-bold px-3 py-4 rounded-lg gap-2 w-1/2'
              }>
              <div>
                <IconLock className={'stroke-[#107E28] fill-[#107E28]'} />
              </div>
              <div className={'flex flex-col'}>
                <span>This is a secure upload.</span>
                <div>
                  Your file will be processed locally on your computer and {'\n'}it will
                  be sent over a secure connection.
                </div>
              </div>
            </div>
          </div>
        )}
        <div className={'flex flex-col items-center justify-end'}>
          <div className={'text-web-gray font-medium text-xs'}>
            Need help? Contact us at{' '}
            <a
              href={'mailto:info@link1data.com'}
              className={'text-primary-xiketic font-bold'}>
              info@link1data.com
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SecureUpload
